// Organization Collection Service
import { createCollectionService, orderBy, where } from '../firebase/firestore';
import type { Organization, OrganizationMember, Project } from '@/types';
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

const orgService = createCollectionService<Organization>('organizations');

export const organizationsService = {
  ...orgService,

  // Get organization by slug (for public portal URLs)
  getBySlug: async (slug: string): Promise<Organization | null> => {
    const orgs = await orgService.getAll([where('slug', '==', slug)]);
    return orgs[0] || null;
  },

  // Get organization members
  getMembers: async (orgId: string): Promise<OrganizationMember[]> => {
    const membersRef = collection(db, 'organizations', orgId, 'members');
    const snapshot = await getDocs(membersRef);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as OrganizationMember);
  },

  // Get member by user ID
  getMember: async (orgId: string, userId: string): Promise<OrganizationMember | null> => {
    const memberRef = doc(db, 'organizations', orgId, 'members', userId);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) return null;
    return { id: memberSnap.id, ...memberSnap.data() } as OrganizationMember;
  },

  // Add member to organization
  addMember: async (
    orgId: string,
    userId: string,
    data: Omit<OrganizationMember, 'id' | 'organizationId' | 'userId' | 'createdAt' | 'updatedAt'>
  ): Promise<void> => {
    const memberRef = doc(db, 'organizations', orgId, 'members', userId);
    await setDoc(memberRef, {
      ...data,
      organizationId: orgId,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  // Update member role
  updateMemberRole: async (
    orgId: string,
    userId: string,
    role: OrganizationMember['role']
  ): Promise<void> => {
    const memberRef = doc(db, 'organizations', orgId, 'members', userId);
    await updateDoc(memberRef, {
      role,
      updatedAt: serverTimestamp(),
    });
  },

  // Remove member from organization
  removeMember: async (orgId: string, userId: string): Promise<void> => {
    const memberRef = doc(db, 'organizations', orgId, 'members', userId);
    // Instead of deleting, mark as inactive
    await updateDoc(memberRef, {
      status: 'inactive',
      updatedAt: serverTimestamp(),
    });
  },

  // Get organization projects
  getProjects: async (orgId: string): Promise<Project[]> => {
    const projectsRef = collection(db, 'organizations', orgId, 'projects');
    const snapshot = await getDocs(query(projectsRef, orderBy('createdAt', 'desc')));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Project);
  },

  // Get project by ID
  getProject: async (orgId: string, projectId: string): Promise<Project | null> => {
    const projectRef = doc(db, 'organizations', orgId, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    if (!projectSnap.exists()) return null;
    return { id: projectSnap.id, ...projectSnap.data() } as Project;
  },

  // Get project by API key (for widget authentication)
  getProjectByApiKey: async (apiKey: string): Promise<{ org: Organization; project: Project } | null> => {
    // This would require a separate index/collection for API key lookup
    // For now, we'll search through organizations (not ideal for scale)
    const orgs = await orgService.getAll();
    for (const org of orgs) {
      const projects = await organizationsService.getProjects(org.id);
      const project = projects.find((p) => p.apiKey === apiKey);
      if (project) {
        return { org, project };
      }
    }
    return null;
  },

  // Create project
  createProject: async (
    orgId: string,
    data: Omit<Project, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> => {
    const projectsRef = collection(db, 'organizations', orgId, 'projects');
    const projectRef = doc(projectsRef);
    await setDoc(projectRef, {
      ...data,
      organizationId: orgId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return projectRef.id;
  },

  // Update project
  updateProject: async (orgId: string, projectId: string, data: Partial<Project>): Promise<void> => {
    const projectRef = doc(db, 'organizations', orgId, 'projects', projectId);
    await updateDoc(projectRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete project
  deleteProject: async (orgId: string, projectId: string): Promise<void> => {
    const projectRef = doc(db, 'organizations', orgId, 'projects', projectId);
    await deleteDoc(projectRef);
  },

  // Update usage counts
  updateUsage: async (
    orgId: string,
    field: keyof Organization['usage'],
    delta: number
  ): Promise<void> => {
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      [`usage.${field}`]: delta,
      updatedAt: serverTimestamp(),
    });
  },

  // Check if within plan limits
  checkLimit: async (
    orgId: string,
    limitType: keyof Organization['limits'],
    currentCount: number
  ): Promise<boolean> => {
    const org = await orgService.getById(orgId);
    if (!org) return false;
    const limit = org.limits[limitType];
    if (typeof limit === 'boolean') return limit;
    if (limit === -1) return true; // Unlimited
    return currentCount < limit;
  },
};
